import React, { Fragment, useEffect } from 'react';
import type { FC } from 'react';

import {
	useAnnotations,
	useAnnotationsDispatch,
	useAnnotationManager,
} from '@confluence/annotation-provider-store';
import { useDialogs } from '@confluence/dialogs/entry-points/useDialogs';
import { CommentWarningDialog } from '@confluence/comment-dialogs';
import { useCommentsContentState, useCommentsContentActions } from '@confluence/comment-context';
import { fg } from '@confluence/feature-gating';

import { CreateComponent } from './CreateComponent';
import { ViewComponent } from './ViewComponent';

export const EditorAnnotationProviderComponents: FC = () => {
	const annotationManager = useAnnotationManager();
	const { editorCreateProps, editorViewProps } = useAnnotations();
	const { setEditorCreateProps, setEditorViewProps } = useAnnotationsDispatch();
	const { hasContentChanged } = useCommentsContentState();
	const { resetContentChanged } = useCommentsContentActions();
	const { showModal } = useDialogs();

	// This hook enables the new preemptive gate for the editor comment API manager to catch the situation
	// where the user tries to start a draft while there is unsaved content in the editor.
	useEffect(() => {
		if (fg('platform_editor_comments_api_manager')) {
			if (hasContentChanged) {
				annotationManager?.setPreemptiveGate(() => {
					return new Promise((resolve) => {
						showModal(CommentWarningDialog, {
							onConfirm: () => {
								resetContentChanged();

								!!editorCreateProps && setEditorCreateProps(null);
								!!editorViewProps && setEditorViewProps(null);

								// Allow the draft to be started or the comment viewed.
								resolve(true);
							},
							onCancel: () => {
								// Cancel the start draft or view attempt.
								resolve(false);
							},
							onClose: () => {
								// Just in case the user closes the modal by some other means we need to ensure the
								// promise is resolved. We do not want to leave the preemptive gate open.
								resolve(false);
							},
						});
					});
				});
			} else {
				annotationManager?.setPreemptiveGate(() => Promise.resolve(true));
			}
			return () => {
				annotationManager?.setPreemptiveGate(() => Promise.resolve(true));
			};
		}
	}, [
		hasContentChanged,
		annotationManager,
		editorCreateProps,
		editorViewProps,
		resetContentChanged,
		showModal,
		setEditorCreateProps,
		setEditorViewProps,
	]);

	return (
		<Fragment>
			{editorCreateProps && <CreateComponent {...editorCreateProps} />}
			{editorViewProps && <ViewComponent {...editorViewProps} />}
		</Fragment>
	);
};
